import React, { useState, useCallback, useMemo, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LuMoveDiagonal, LuMoveDiagonal2, LuMoveHorizontal, LuMoveVertical } from "react-icons/lu";
import ApiService from "../../../../services/ApiService";
import Logger from "../../../../services/Logger";
import GridCandEntry from "./GridCandEntry";
import GirdCanAssocConfirmDialog from "./GirdCanAssocConfirmDialog";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { generatePositions, getGridWord } from "./GridCandUtils";
import { resolveTplGridDims, resolveTplGridSize } from "../gridTplSize";
import { DIRECTION_DIAGONAL_LEFT_UP, DIRECTION_DIAGONAL_RIGHT_UP, DIRECTION_HORIZONTAL, DIRECTION_VERTICAL } from "../../../pzl_common/PuzzleUtil";
import AdminGridBoardService, { DRAFT } from "../AdminGridBoardService";

// Normalize French word by removing accents
const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toLowerCase();
};

// Normalize a single letter for comparison (French mode)
const normalizeLetter = (letter, language) => {
    if (!letter || typeof letter !== 'string') return letter;
    if (language === 'french') {
        return normalizeFrenchWord(letter);
    }
    return letter.toLowerCase();
};

// Helper function to map word characters to positions correctly
const mapWordToPositions = (positions, word, direction, isReversed) => {
    const isDiagonal = direction === DIRECTION_DIAGONAL_RIGHT_UP || direction === DIRECTION_DIAGONAL_LEFT_UP;
    const length = positions.length;
    
    for (let i = 0; i < length; i++) {
        let charIndex;
        if (isReversed && isDiagonal) {
            // For diagonal directions with isReversed=true:
            // Position i=0 is the start (bottom-left/right), should have word[0]
            charIndex = i;
        } else if (isReversed && !isDiagonal) {
            // For horizontal/vertical with isReversed=true:
            // Position i=0 is the leftmost/bottommost, should have word[length-1]
            charIndex = length - 1 - i;
        } else {
            // For non-reversed (all directions):
            // Position i=0 is the start, should have word[0]
            charIndex = i;
        }
        
        if (charIndex >= 0 && charIndex < word.length) {
            positions[i].value = word[charIndex].toLowerCase();
        }
    }
    
    return positions;
};

// Helper function to get direction-specific label
const getDirectionLabel = (direction, leftToRight) => {
    if (direction === 'scattered') {
        return "Scattered Placement";
    }
    switch (direction) {
        case DIRECTION_HORIZONTAL:
            return leftToRight ? "Left to Right" : "Right to Left";
        case DIRECTION_VERTICAL:
            return leftToRight ? "Top to Bottom" : "Bottom to Top";
        case DIRECTION_DIAGONAL_RIGHT_UP:
            return leftToRight ? "Top-Right to Bottom-Left" : "Bottom-Left to Top-Right";
        case DIRECTION_DIAGONAL_LEFT_UP:
            return leftToRight ? "Top-Left to Bottom-Right" : "Bottom-Right to Top-Left";
        default:
            return leftToRight ? "Forward" : "Reverse";
    }
};

export default function GridCandAI({
    onWordAdd,
    onWordSelect,
    selectedManualWord,
    onShowSnackbar,
    square,
    gridTpl,
    onRefresh
}) {
    const { rows: gridRowsCount, cols: gridColsCount } = resolveTplGridDims(gridTpl);
    const gridSize = resolveTplGridSize(gridTpl);
    const gridCellCount = gridRowsCount * gridColsCount;
    const [aiWord, setAiWord] = useState('');
    const [aiCandidates, setAiCandidates] = useState([]);
    const [aiLoading, setAiLoading] = useState(false);
    const [manualRow, setManualRow] = useState(0);
    const [manualCol, setManualCol] = useState(0);
    const [manualDirection, setManualDirection] = useState(DIRECTION_HORIZONTAL);
    const [leftToRight, setLeftToRight] = useState(true);
    const [filterByLength, setFilterByLength] = useState(false);
    const [autoPlacing, setAutoPlacing] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Scattered placement dialog state
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [wordToPlace, setWordToPlace] = useState(null);

    // Calculate actual filled cells by considering unique coordinates
    const calculateFilledCells = useCallback(() => {
        if (!gridTpl?.filledCoordinates) return 0;
        try {
            const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
            const uniqueCells = new Set();

            filledCoordinates.forEach(word => {
                if (word.text && word.positions) {
                    // Add each position coordinate to the set
                    word.positions.forEach(pos => {
                        uniqueCells.add(`${pos.row}-${pos.col}`);
                    });
                }
            });

            return uniqueCells.size;
        } catch (error) {
            Logger.error("Error calculating filled cells:", error);
            return 0;
        }
    }, [gridTpl?.filledCoordinates]);

    const emptyCellsCount = gridCellCount - calculateFilledCells();

    // Handle AI word search
    const handleAISearch = useCallback(async () => {
        if (!aiWord.trim()) {
            onShowSnackbar("Please enter a word", "error");
            return;
        }

        setAiLoading(true);
        setAiCandidates([]);
        setFilterByLength(false); // Reset filter when fetching new candidates

        try {
            const language = gridTpl?.language || 'english';
            const params = {
                title: aiWord.trim(),
                language: language
            };
            const result = await ApiService.getData("/cr/bedrock/word-associations", params);
            
            if (result && result.associations && Array.isArray(result.associations)) {
                // Transform associations into candidate format
                const transformedCandidates = [];
                result.associations.forEach((word, index) => {
                    // Normalize word
                    let normalizedWord = word;
                    if (language === 'french') {
                        normalizedWord = normalizeFrenchWord(word);
                    } else {
                        normalizedWord = word.toLowerCase();
                    }

                    // Remove hyphens for grid placement
                    const gridWord = getGridWord(normalizedWord);

                    // For scattered placement, we don't need to generate positions here
                    if (manualDirection === 'scattered') {
                        transformedCandidates.push({
                            word: word, // Display version (original)
                            gridWord: gridWord, // Grid version without hyphens
                            score: result.associations.length - index,
                            startRow: 0, // Not used for scattered
                            startCol: 0, // Not used for scattered
                            direction: 'scattered',
                            positions: [], // Will be generated on placement
                            isReversed: false
                        });
                    } else {
                        // Add normal word (left-to-right/top-to-bottom)
                        if (leftToRight) {
                            transformedCandidates.push({
                                word: word, // Display version (original)
                                gridWord: gridWord, // Grid version without hyphens
                                score: result.associations.length - index,
                                startRow: manualRow,
                                startCol: manualCol,
                                direction: manualDirection,
                                positions: generatePositions(manualRow, manualCol, manualDirection, gridWord.length, gridWord, false),
                                isReversed: false
                            });
                        }
                        // Add reversed word (right-to-left/bottom-to-top) - only if leftToRight is disabled
                        if (!leftToRight) {
                            let positions = generatePositions(manualRow, manualCol, manualDirection, gridWord.length, "", true);
                            mapWordToPositions(positions, gridWord, manualDirection, true);
                            
                            transformedCandidates.push({
                                word: word, // Display version (original)
                                gridWord: gridWord, // Grid version without hyphens
                                score: result.associations.length - index,
                                startRow: manualRow,
                                startCol: manualCol,
                                direction: manualDirection,
                                positions: positions,
                                isReversed: true
                            });
                        }
                    }
                });

                setAiCandidates(transformedCandidates);

                if (transformedCandidates.length === 0) {
                    onShowSnackbar("No associations found", "info");
                } else {
                    onShowSnackbar(`Found ${transformedCandidates.length} associations`, "success");
                }
            } else {
                onShowSnackbar("No associations found for this word", "info");
            }
        } catch (error) {
            Logger.error("Error fetching word associations:", error);
            onShowSnackbar("Error fetching word associations. Please try again.", "error");
        } finally {
            setAiLoading(false);
        }
    }, [aiWord, gridTpl?.language, manualRow, manualCol, manualDirection, leftToRight, onShowSnackbar]);

    // Get already used words from the puzzle
    const usedWordsInPuzzle = useMemo(() => {
        const usedWords = new Set();
        if (gridTpl?.filledCoordinates) {
            try {
                const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                filledCoordinates.forEach(w => {
                    if (w.text) {
                        // Normalize word (remove hyphens, uppercase) for comparison
                        const text = getGridWord(w.text).toUpperCase();
                        usedWords.add(text);
                    }
                });
            } catch (error) {
                Logger.error("Error parsing filledCoordinates:", error);
            }
        }
        return usedWords;
    }, [gridTpl?.filledCoordinates]);

    // Update positions when row/col/direction changes
    const memoizedCandidates = useMemo(() => {
        if (!aiCandidates || aiCandidates.length === 0) {
            return [];
        }
        
        let filteredCandidates = aiCandidates;
        
        // Filter out words that are already in the puzzle
        filteredCandidates = filteredCandidates.filter(candidate => {
            const gridWord = (candidate.gridWord || getGridWord(candidate.word)).toUpperCase();
            return !usedWordsInPuzzle.has(gridWord);
        });
        
        // Filter by length if filterByLength is enabled
        if (filterByLength) {
            filteredCandidates = filteredCandidates.filter(candidate => {
                const gridWord = candidate.gridWord || getGridWord(candidate.word);
                return gridWord.length === emptyCellsCount;
            });
        }
        
        return filteredCandidates
            .map(candidate => {
                // For scattered placement, positions are empty and will be generated on placement
                if (candidate.direction === 'scattered') {
                    return {
                        ...candidate,
                        key: `${candidate.word}-scattered`
                    };
                }
                
                // Recalculate positions based on current manualRow, manualCol, and direction
                const gridWord = candidate.gridWord || getGridWord(candidate.word);
                const updatedPositions = generatePositions(
                    manualRow,
                    manualCol,
                    manualDirection,
                    gridWord.length,
                    "",
                    candidate.isReversed
                );
                mapWordToPositions(updatedPositions, gridWord, manualDirection, candidate.isReversed);
                
                return {
                    ...candidate,
                    startRow: manualRow,
                    startCol: manualCol,
                    direction: manualDirection,
                    positions: updatedPositions,
                    key: `${candidate.word}-${manualRow}-${manualCol}-${manualDirection}-${candidate.isReversed}`
                };
            })
            .sort((a, b) => (b.score || 0) - (a.score || 0)); // Sort by score descending
    }, [aiCandidates, manualRow, manualCol, manualDirection, filterByLength, emptyCellsCount, usedWordsInPuzzle, refreshTrigger]);

    // Update form fields when a word is selected externally
    useEffect(() => {
        if (selectedManualWord) {
            setManualRow(selectedManualWord.startRow || 0);
            setManualCol(selectedManualWord.startCol || 0);
            setManualDirection(selectedManualWord.direction || DIRECTION_HORIZONTAL);
            setLeftToRight(!selectedManualWord.isReversed);
        }
    }, [selectedManualWord]);

    const handleDirectionChange = (newDirection) => {
        setManualDirection(newDirection);
        if (selectedManualWord && newDirection !== 'scattered') {
            const gridWord = selectedManualWord.gridWord || getGridWord(selectedManualWord.word);
            let positions = generatePositions(manualRow, manualCol, newDirection, gridWord.length, "", selectedManualWord.isReversed);
            mapWordToPositions(positions, gridWord, newDirection, selectedManualWord.isReversed);
            const updatedWord = {
                ...selectedManualWord,
                direction: newDirection,
                positions: positions
            };
            onWordSelect(updatedWord);
        } else if (selectedManualWord && newDirection === 'scattered') {
            // For scattered, clear the selected word since positions can't be generated
            onWordSelect(null);
        }
    };

    const handleLeftToRightChange = (newLeftToRight) => {
        setLeftToRight(newLeftToRight);
        if (selectedManualWord) {
            const isReversed = !newLeftToRight;
            const gridWord = selectedManualWord.gridWord || getGridWord(selectedManualWord.word);
            let positions = generatePositions(manualRow, manualCol, manualDirection, gridWord.length, "", isReversed);
            mapWordToPositions(positions, gridWord, manualDirection, isReversed);
            const updatedWord = {
                ...selectedManualWord,
                isReversed: isReversed,
                positions: positions
            };
            onWordSelect(updatedWord);
        }
    };

    // Check if word can fit at the specified position, matching existing characters
    const checkWordCanFit = useCallback((candidate) => {
        if (!square || square.length === 0) return true; // If no square, allow placement
        
        const language = gridTpl?.language || 'english';
        const gridWord = (candidate.gridWord || getGridWord(candidate.word)).toLowerCase();
        
        // For scattered placement, we don't need to check here (handled separately)
        if (candidate.direction === 'scattered') {
            return true;
        }
        
        // Check each position
        if (!candidate.positions || candidate.positions.length === 0) {
            return false; // No positions generated
        }
        
        // Get set of already filled positions from filledCoordinates
        const filledPositions = new Set();
        if (gridTpl?.filledCoordinates) {
            try {
                const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                filledCoordinates.forEach(w => {
                    if (w.positions) {
                        w.positions.forEach(pos => {
                            if (pos.value) {
                                filledPositions.add(`${pos.row}-${pos.col}`);
                            }
                        });
                    }
                });
            } catch (error) {
                Logger.error("Error parsing filledCoordinates:", error);
            }
        }
        
        for (let i = 0; i < candidate.positions.length; i++) {
            const pos = candidate.positions[i];
            const expectedChar = gridWord[i];
            const posKey = `${pos.row}-${pos.col}`;
            
            // Check bounds
            if (pos.row < 0 || pos.row >= gridSize || pos.col < 0 || pos.col >= gridSize) {
                return false; // Out of bounds
            }
            
            // Check if position is already filled in filledCoordinates
            if (filledPositions.has(posKey)) {
                // Position is already used - check if character matches
                const existingChar = square[pos.row][pos.col];
                if (existingChar && existingChar !== "") {
                    const normalizedExisting = normalizeLetter(existingChar, language);
                    const normalizedExpected = normalizeLetter(expectedChar, language);
                    if (normalizedExisting !== normalizedExpected) {
                        return false; // Character mismatch
                    }
                } else {
                    // Position is in filledCoordinates but not in square - still check if we can match
                    // This shouldn't happen normally, but handle it
                    return false; // Position already used
                }
            } else {
                // Position is not in filledCoordinates - check square
                const existingChar = square[pos.row][pos.col];
                if (existingChar && existingChar !== "") {
                    // Character exists in square - must match
                    const normalizedExisting = normalizeLetter(existingChar, language);
                    const normalizedExpected = normalizeLetter(expectedChar, language);
                    if (normalizedExisting !== normalizedExpected) {
                        return false; // Character mismatch
                    }
                }
                // If position is empty, it's fine - we can place the character
            }
        }
        
        return true;
    }, [square, gridTpl?.filledCoordinates, gridTpl?.language]);

    const handleManualWordAdd = (candidate) => {
        // For scattered placement, open confirmation dialog
        if (candidate.direction === 'scattered') {
            setWordToPlace(candidate.gridWord || candidate.word);
            setConfirmDialogOpen(true);
            return;
        }

        // Validate that the word can fit at the specified position
        if (!checkWordCanFit(candidate)) {
            onShowSnackbar(
                `Word "${candidate.word}" cannot be placed at the specified position. Characters don't match existing cells.`,
                "error"
            );
            return;
        }

        onWordAdd(candidate);
        // Reset form after successful add
        setAiWord('');
        setAiCandidates([]);
    };

    // Auto-place all candidates in the puzzle
    const handleAutoPlaceAll = useCallback(async () => {
        if (!square || square.length === 0 || memoizedCandidates.length === 0) {
            onShowSnackbar("No candidates to place or puzzle grid is not available", "info");
            return;
        }

        setAutoPlacing(true);
        let placedCount = 0;
        let skippedCount = 0;

        try {
            const language = gridTpl?.language || 'english';
            
            // Get current filled positions to track what's been used
            const usedPositions = new Set();
            const usedWords = new Set();
            
            // Initialize from current square
            if (square) {
                for (let row = 0; row < gridSize; row++) {
                    for (let col = 0; col < gridSize; col++) {
                        const char = square[row][col];
                        if (char && char !== "") {
                            usedPositions.add(`${row}-${col}`);
                        }
                    }
                }
            }
            
            // Also check filledCoordinates from database
            if (gridTpl?.filledCoordinates) {
                try {
                    const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                    filledCoordinates.forEach(w => {
                        if (w.text) {
                            const text = getGridWord(w.text).toUpperCase();
                            usedWords.add(text);
                        }
                        if (w.positions) {
                            w.positions.forEach(pos => {
                                if (pos.value) {
                                    usedPositions.add(`${pos.row}-${pos.col}`);
                                }
                            });
                        }
                    });
                } catch (error) {
                    Logger.error("Error parsing filledCoordinates:", error);
                }
            }

            // Helper function to check if a position is available
            const isPositionAvailable = (row, col) => {
                return !usedPositions.has(`${row}-${col}`);
            };

            // Helper function to check if word fits at a position
            const checkWordFits = (word, startRow, startCol, direction) => {
                const positions = generatePositions(startRow, startCol, direction, word.length, word);
                for (let i = 0; i < positions.length; i++) {
                    const pos = positions[i];
                    if (pos.row < 0 || pos.row >= gridSize || pos.col < 0 || pos.col >= gridSize) {
                        return false; // Out of bounds
                    }
                    const expectedChar = word[i];
                    if (!isPositionAvailable(pos.row, pos.col)) {
                        // Check if existing character matches
                        const existingChar = square[pos.row][pos.col];
                        if (existingChar && existingChar !== "") {
                            const normalizedExisting = normalizeLetter(existingChar, language);
                            const normalizedExpected = normalizeLetter(expectedChar, language);
                            if (normalizedExisting !== normalizedExpected) {
                                return false; // Character mismatch
                            }
                        } else {
                            return false; // Position is already used (in filledCoordinates but not in square)
                        }
                    }
                }
                return true;
            };

            // Try to place each candidate
            for (const candidate of memoizedCandidates) {
                // Skip scattered words for auto-placement
                if (candidate.direction === 'scattered') {
                    skippedCount++;
                    continue;
                }

                const gridWord = (candidate.gridWord || getGridWord(candidate.word)).toUpperCase();
                
                // Skip if word is already used
                if (usedWords.has(gridWord)) {
                    skippedCount++;
                    continue;
                }

                // Try all possible positions and directions
                const directions = [DIRECTION_HORIZONTAL, DIRECTION_VERTICAL, DIRECTION_DIAGONAL_RIGHT_UP, DIRECTION_DIAGONAL_LEFT_UP];
                let placed = false;

                for (const direction of directions) {
                    // Try all possible starting positions
                    for (let row = 0; row < gridSize && !placed; row++) {
                        for (let col = 0; col < gridSize && !placed; col++) {
                            if (checkWordFits(gridWord, row, col, direction)) {
                                // Generate positions for this word
                                const positions = generatePositions(
                                    row,
                                    col,
                                    direction,
                                    gridWord.length,
                                    gridWord
                                );

                                // Create candidate with positions
                                const candidateWithPositions = {
                                    ...candidate,
                                    word: candidate.word,
                                    gridWord: gridWord,
                                    positions: positions,
                                    startRow: row,
                                    startCol: col,
                                    direction: direction,
                                    isReversed: false
                                };

                                // Add the word
                                try {
                                    const addResult = onWordAdd(candidateWithPositions);
                                    if (addResult && typeof addResult.then === 'function') {
                                        await addResult;
                                    } else {
                                        await new Promise(resolve => setTimeout(resolve, 200));
                                    }
                                    
                                    // Mark positions as used
                                    positions.forEach(pos => {
                                        usedPositions.add(`${pos.row}-${pos.col}`);
                                    });
                                    usedWords.add(gridWord);
                                    placedCount++;
                                    placed = true;
                                    
                                    // Small delay to allow the puzzle to update
                                    await new Promise(resolve => setTimeout(resolve, 100));
                                    
                                    break;
                                } catch (error) {
                                    Logger.error(`Error placing word "${candidate.word}":`, error);
                                    // Continue to next position
                                }
                            }
                        }
                    }
                    if (placed) break;
                }

                if (!placed) {
                    skippedCount++;
                }
            }

            // Refresh puzzle data
            if (onRefresh && placedCount > 0) {
                onRefresh();
            }

            onShowSnackbar(
                `Auto-placed ${placedCount} word(s). ${skippedCount} word(s) skipped.`,
                placedCount > 0 ? "success" : "info"
            );
        } catch (error) {
            Logger.error("Error in auto-placement:", error);
            onShowSnackbar("Error during auto-placement: " + error.message, "error");
        } finally {
            setAutoPlacing(false);
        }
    }, [memoizedCandidates, square, gridTpl, onWordAdd, onShowSnackbar, onRefresh]);

    // Handle confirmation and place word in puzzle (scattered placement)
    const handleConfirmWordPlacement = async (updatedTitle) => {
        if (!wordToPlace) return;

        try {
            // Normalize French words by removing accents
            const language = gridTpl?.language || 'english';
            let normalizedWord = getGridWord(wordToPlace);
            if (language === 'french') {
                normalizedWord = normalizeFrenchWord(normalizedWord);
            } else {
                normalizedWord = normalizedWord.toLowerCase();
            }

            const wordLength = normalizedWord.length;
            const positions = [];

            // Get current filled positions to avoid conflicts
            const currentFilledPositions = new Set();

            // Check the actual puzzle grid for filled cells
            if (square) {
                for (let row = 0; row < gridSize; row++) {
                    for (let col = 0; col < gridSize; col++) {
                        const char = square[row][col];
                        if (char && char !== "") {
                            currentFilledPositions.add(`${row}-${col}`);
                        }
                    }
                }
            }

            // Also check filledCoordinates from database
            if (gridTpl?.filledCoordinates) {
                try {
                    const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
                    filledCoordinates.forEach(w => {
                        if (w.positions) {
                            w.positions.forEach(pos => {
                                if (pos.value) {
                                    const posKey = `${pos.row}-${pos.col}`;
                                    currentFilledPositions.add(posKey);
                                }
                            });
                        }
                    });
                } catch (error) {
                    Logger.error("Error parsing filled coordinates:", error);
                }
            }

            // Count total empty cells
            let totalEmptyCells = 0;
            for (let row = 0; row < gridSize; row++) {
                for (let col = 0; col < gridSize; col++) {
                    const positionKey = `${row}-${col}`;
                    if (!currentFilledPositions.has(positionKey)) {
                        totalEmptyCells++;
                    }
                }
            }

            // First, check if we have enough empty cells
            if (totalEmptyCells < wordLength) {
                throw new Error(`Not enough empty cells to place the word "${wordToPlace}". Found ${totalEmptyCells} empty cells, need ${wordLength}`);
            }

            // Place characters from top-left to bottom-right in empty cells
            let charIndex = 0;
            for (let row = 0; row < gridSize && charIndex < wordLength; row++) {
                for (let col = 0; col < gridSize && charIndex < wordLength; col++) {
                    const positionKey = `${row}-${col}`;

                    // Check if this position is empty
                    if (!currentFilledPositions.has(positionKey)) {
                        positions.push({
                            row: row,
                            col: col,
                            value: normalizedWord[charIndex]
                        });
                        // Add to currentFilledPositions to prevent reuse
                        currentFilledPositions.add(positionKey);
                        charIndex++;
                    }
                }
            }

            // Make sure we placed all characters
            if (charIndex < wordLength) {
                throw new Error(`Not enough empty cells to place the word "${wordToPlace}". Found ${charIndex} empty cells, need ${wordLength}`);
            }

            // Create word data with sequential positions
            const wordData = {
                word: normalizedWord,
                text: normalizedWord,
                positions: positions,
                direction: 'scattered',
                length: normalizedWord.length,
                startRow: positions[0]?.row || 0,
                startCol: positions[0]?.col || 0
            };

            // Add the word to the puzzle and wait for it to complete
            if (onWordAdd) {
                try {
                    // Call onWordAdd and await if it returns a promise
                    const addResult = onWordAdd(wordData);
                    if (addResult && typeof addResult.then === 'function') {
                        await addResult;
                    } else {
                        // Give a small delay to ensure the word is processed if not async
                        await new Promise(resolve => setTimeout(resolve, 200));
                    }
                    
                    // Update puzzle type to HIDDEN_MESSAGE when word is placed, including title and hiddenMessage
                    if (gridTpl?.id) {
                        try {
                            const currentState = gridTpl.state || DRAFT;
                            const titleToUse = updatedTitle !== undefined ? updatedTitle : (gridTpl.title || '');
                            // Use the normalized word (lowercase) as the hidden message
                            await AdminGridBoardService.updateHwTplState(gridTpl.id, currentState, 'HIDDEN_MESSAGE', null, titleToUse, normalizedWord);
                            
                            // Trigger refresh to update the puzzle data with the new type and hiddenMessage
                            if (onRefresh) {
                                onRefresh();
                            }
                            
                            onShowSnackbar(`Word "${wordToPlace}" placed in puzzle. Puzzle type set to HIDDEN_MESSAGE.`, "success");
                        } catch (error) {
                            Logger.error("Error updating puzzle type to HIDDEN_MESSAGE:", error);
                            onShowSnackbar(`Word "${wordToPlace}" placed in puzzle, but failed to update puzzle type`, "warning");
                        }
                    } else {
                        onShowSnackbar(`Word "${wordToPlace}" placed in puzzle`, "success");
                    }
                } catch (error) {
                    Logger.error("Error adding word to puzzle:", error);
                    onShowSnackbar("Error adding word to puzzle: " + error.message, "error");
                }
            }

            // Reset form and candidates after successful add
            setAiWord('');
            setAiCandidates([]);
            setConfirmDialogOpen(false);
            setWordToPlace(null);
        } catch (error) {
            Logger.error("Error placing scattered word:", error);
            onShowSnackbar("Error placing word: " + error.message, "error");
            setConfirmDialogOpen(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, height: '100%', flex: 1 }}>
            {/* AI Word Input Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                        label="Ask AI for a Word or sentence"
                        value={aiWord}
                        onChange={(e) => setAiWord(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && aiWord.trim() && !aiLoading) {
                                handleAISearch();
                            }
                        }}
                        placeholder="Enter a word or sentence"
                        helperText="Enter a word or sentence to get AI-generated associations"
                        size="small"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title={aiLoading ? 'Searching...' : 'Get Associations'}>
                                            <span>
                                                <IconButton
                                                    onClick={handleAISearch}
                                                    disabled={!aiWord.trim() || aiLoading}
                                                    color="primary"
                                                    size="small"
                                                >
                                                    {aiLoading ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <AutoAwesomeIcon />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                        label="Row"
                        type="number"
                        value={manualRow}
                        onChange={(e) => setManualRow(Math.max(0, Math.min(gridSize - 1, parseInt(e.target.value) || 0)))}
                        inputProps={{ min: 0, max: gridSize - 1 }}
                        size="small"
                        sx={{ width: 80 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <TextField
                            label="Column"
                            type="number"
                            value={manualCol}
                            onChange={(e) => setManualCol(Math.max(0, Math.min(gridSize - 1, parseInt(e.target.value) || 0)))}
                            inputProps={{ min: 0, max: gridSize - 1 }}
                            size="small"
                            sx={{ width: 80 }}
                        />
                        <Tooltip title="Refresh candidates positions">
                            <IconButton
                                onClick={() => {
                                    // Force update by incrementing refresh trigger
                                    // This will cause memoizedCandidates to recalculate
                                    setRefreshTrigger(prev => prev + 1);
                                }}
                                size="small"
                                color="primary"
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                variant="caption"
                                color="error"
                                onClick={() => setFilterByLength(!filterByLength)}
                                sx={{
                                    fontWeight: 'bold',
                                    letterSpacing: 0.5,
                                    fontSize: '0.75rem',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: (theme) => filterByLength 
                                        ? theme.palette.error.main 
                                        : theme.palette.error[theme.palette.mode === 'dark' ? 800 : 50],
                                    color: filterByLength ? 'white' : 'error.main',
                                    border: (theme) => `1px solid ${theme.palette.error.main}`,
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: (theme) => theme.palette.error.main,
                                        color: 'white'
                                    }
                                }}
                            >
                                Empty: {emptyCellsCount}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Direction:
                    </Typography>
                    <RadioGroup
                        row
                        value={manualDirection}
                        onChange={(e) => handleDirectionChange(e.target.value)}
                    >
                        <Tooltip title="Horizontal">
                            <FormControlLabel
                                value={DIRECTION_HORIZONTAL}
                                control={<Radio size="small" />}
                                label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveHorizontal /></Box>}
                            />
                        </Tooltip>
                        <Tooltip title="Vertical">
                            <FormControlLabel
                                value={DIRECTION_VERTICAL}
                                control={<Radio size="small" />}
                                label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveVertical /></Box>}
                            />
                        </Tooltip>
                        <Tooltip title="Diagonal Right Up">
                            <FormControlLabel
                                value={DIRECTION_DIAGONAL_RIGHT_UP}
                                control={<Radio size="small" />}
                                label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveDiagonal /></Box>}
                            />
                        </Tooltip>
                        <Tooltip title="Diagonal Left Up">
                            <FormControlLabel
                                value={DIRECTION_DIAGONAL_LEFT_UP}
                                control={<Radio size="small" />}
                                label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveDiagonal2 /></Box>}
                            />
                        </Tooltip>
                        <Tooltip title="Scattered Placement">
                            <FormControlLabel
                                value="scattered"
                                control={<Radio size="small" />}
                                label="Scattered"
                            />
                        </Tooltip>
                    </RadioGroup>
                    {manualDirection !== 'scattered' && (
                        <ToggleButtonGroup
                            value={leftToRight ? "leftToRight" : "rightToLeft"}
                            exclusive
                            onChange={(e, newValue) => {
                                if (newValue !== null) {
                                    handleLeftToRightChange(newValue === "leftToRight");
                                }
                            }}
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            <ToggleButton value="leftToRight">
                                {manualDirection === DIRECTION_HORIZONTAL ? "Left to Right" :
                                 manualDirection === DIRECTION_VERTICAL ? "Top to Bottom" :
                                 manualDirection === DIRECTION_DIAGONAL_RIGHT_UP ? "Top-Right to Bottom-Left" :
                                 manualDirection === DIRECTION_DIAGONAL_LEFT_UP ? "Top-Left to Bottom-Right" :
                                 "Forward"}
                            </ToggleButton>
                            <ToggleButton value="rightToLeft">
                                {manualDirection === DIRECTION_HORIZONTAL ? "Right to Left" :
                                 manualDirection === DIRECTION_VERTICAL ? "Bottom to Top" :
                                 manualDirection === DIRECTION_DIAGONAL_RIGHT_UP ? "Bottom-Left to Top-Right" :
                                 manualDirection === DIRECTION_DIAGONAL_LEFT_UP ? "Bottom-Right to Top-Left" :
                                 "Reverse"}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    {manualDirection === 'scattered' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {getDirectionLabel(manualDirection, leftToRight)}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* AI Candidates Section */}
            <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                        Word Candidates
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={autoPlacing ? <CircularProgress size={16} /> : <PlaylistAddIcon />}
                        onClick={handleAutoPlaceAll}
                        disabled={autoPlacing || memoizedCandidates.length === 0 || aiLoading}
                        sx={{ minWidth: 120 }}
                    >
                        {autoPlacing ? 'Placing...' : 'Add All'}
                    </Button>
                </Box>
                {aiLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Fetching associations...</Typography>
                    </Box>
                ) : (
                    <List sx={{ flex: 1, overflow: 'auto' }}>
                        {memoizedCandidates.length === 0 ? (
                            <ListItem>
                                <ListItemText
                                    primary="No candidates found"
                                    secondary="Enter a word and click the icon to get AI associations"
                                />
                            </ListItem>
                        ) : (
                            memoizedCandidates.map((candidate) => (
                                <GridCandEntry
                                    key={candidate.key}
                                    candidate={candidate}
                                    isSelected={selectedManualWord?.word === candidate.word}
                                    onWordSelect={onWordSelect}
                                    onWordAdd={handleManualWordAdd}
                                    mode="manual"
                                />
                            ))
                        )}
                    </List>
                )}
            </Box>
            
            {/* Confirmation Dialog for Scattered Placement */}
            <GirdCanAssocConfirmDialog
                open={confirmDialogOpen}
                setOpen={setConfirmDialogOpen}
                word={wordToPlace}
                title={gridTpl?.title || ''}
                gridTplId={gridTpl?.id}
                currentState={gridTpl?.state || DRAFT}
                puzzleType="HIDDEN_MESSAGE"
                onConfirm={handleConfirmWordPlacement}
                language={gridTpl?.language || 'english'}
            />
        </Box>
    );
}

